import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import AccessTimeIcon from "@mui/icons-material/AccessTime";
import Cookies from "js-cookie";

import Dialog from "@musicfy/components/Dialog";
import Icon, { type IIconName } from "@musicfy/components/Icon";
import StartCheckoutFlowButton from "@musicfy/components/PaymentButtons/StartCheckoutFlowButton";
import { useAnalytics } from "@musicfy/libs/AnalyticsProvider";
import { useSubscriptionContext } from "@musicfy/libs/SubscriptionProvider";

import {
  VALUE_MOMENT_COOKIE,
  VALUE_MOMENT_EVENT,
  type ValueMomentDetail,
  computeOfferEnd,
  isFreePlan,
  parseAnchor,
  shouldShowValueMoment,
  splitRemaining,
} from "./valueMoment";
import Card from "../Card";

const PERKS: { icon: IIconName; label: string }[] = [
  { icon: "sound", label: "No watermark, HD" },
  { icon: "cube", label: "Every voice" },
  { icon: "rocket", label: "Commercial license" },
  { icon: "rotation", label: "Longer tracks" },
];

/**
 * In-app upsell fired at the moment of value — a FREE user's generation just
 * completed. Self-contained banner in the MemorialDayBanner mold: same Dialog
 * shell + 4-box countdown + StartCheckoutFlowButton, on the SAME Paddle
 * checkout machinery (no new payment plumbing).
 *
 * Trigger: the generation flow dispatches a `musicfy:value-moment` window
 * CustomEvent on completion (see valueMoment.ts + the InferenceButtons). This
 * component listens, applies the pure-fn gate (free + once-per-48h), and opens.
 * All show/suppress rules live in valueMoment.ts and are unit-tested there.
 */
const ValueMomentUpsell = (): JSX.Element | null => {
  const { trackEvent } = useAnalytics();
  const { subscription } = useSubscriptionContext();

  // Payer-safety gate: show ONLY to an affirmatively-known free user. A null
  // subscription (loading OR the plan query errored) is "unknown" → suppress,
  // never "free". This is the authoritative guard — the dispatcher's call-site
  // check is a cheap early-out, this is the one that must be correct. (HIGH-1/2:
  // treating null as free would let a payer whose plan fetch blips see the modal
  // AND get tagged with value_moment_shown → an upsell email. Both keyed off
  // this same corrected value, so a payer fires neither.)
  const isFree = isFreePlan(subscription?.plan);

  const [open, setOpen] = useState(false);
  const [ctx, setCtx] = useState<ValueMomentDetail>({});
  const [offerEnd, setOfferEnd] = useState<number | null>(null);
  const [remaining, setRemaining] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const handleOpen = useCallback(
    (detail: ValueMomentDetail) => {
      const anchorRaw = Cookies.get(VALUE_MOMENT_COOKIE);
      const lastShownAt = parseAnchor(anchorRaw);

      if (!shouldShowValueMoment({ isFree, lastShownAt, now: Date.now() })) {
        return;
      }

      // Re-anchor on EVERY show: the 48h clock restarts from this show, so the
      // next generation within 48h is suppressed and the countdown is a fresh
      // 48h (never a stale/expired window). (HIGH-3/MED-4/MED-5: the old
      // "only set the cookie when null" logic left the anchor pinned to the
      // first show — after 48h it re-fired on every generation for 5 days with a
      // dead 00:00:00 countdown until the cookie expired.)
      const anchor = Date.now();
      Cookies.set(VALUE_MOMENT_COOKIE, String(anchor), { expires: 7 });

      const end = computeOfferEnd(anchor);
      setCtx(detail);
      setOfferEnd(end);
      setRemaining(Math.max(0, end - Date.now()));
      setOpen(true);
      trackEvent("value_moment_shown", {
        topVoice: detail.topVoice ?? null,
        hasTrackName: !!detail.trackName,
      });
    },
    [isFree, trackEvent]
  );

  // Listen for the generation-complete signal.
  useEffect(() => {
    const listener = (e: Event) => {
      const detail = (e as CustomEvent<ValueMomentDetail>).detail ?? {};
      handleOpen(detail);
    };
    window.addEventListener(VALUE_MOMENT_EVENT, listener);
    return () => window.removeEventListener(VALUE_MOMENT_EVENT, listener);
  }, [handleOpen]);

  // Tick the countdown while open.
  useEffect(() => {
    if (!open || offerEnd == null) return;
    timerRef.current = setInterval(() => {
      setRemaining(Math.max(0, offerEnd - Date.now()));
    }, 1000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [open, offerEnd]);

  const handleClose = useCallback(() => setOpen(false), []);

  const { days, hours, minutes, seconds } = useMemo(
    () => splitRemaining(remaining),
    [remaining]
  );

  if (!open || !isFree) return null;

  const pad = (n: number) => String(n).padStart(2, "0");
  const headline = ctx.trackName ? "love it? get it clean." : "get it clean.";
  const subline = ctx.topVoice
    ? `your ${ctx.topVoice} ${
        ctx.trackName ? "cover" : "track"
      } sounds great — lose the watermark, get the HD download.`
    : "lose the watermark and download the full HD track.";

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title="You made something good"
      TitleIcon={<AccessTimeIcon />}
      width="468px"
      primaryButton={
        <StartCheckoutFlowButton origin="value-moment-watermark" fullWidth>
          Unlock the clean version — 50% off
        </StartCheckoutFlowButton>
      }
    >
      <div className="font-bold text-24">{headline}</div>
      <div className="mt-2 text-white/80 text-14">{subline}</div>

      <div className="mt-5 grid grid-cols-2 gap-3">
        {PERKS.map((p) => (
          <div key={p.label} className="flex items-center gap-2 text-14">
            <Icon name={p.icon} className="w-5 text-green" />
            <div>{p.label}</div>
          </div>
        ))}
      </div>

      <div className="mt-6 flex items-center gap-2 text-12 text-pink-100">
        <AccessTimeIcon fontSize="small" /> Offer ends in
      </div>
      <div className="mt-2 grid grid-cols-4 gap-2">
        {[
          { v: String(days), l: "Days" },
          { v: pad(hours), l: "Hours" },
          { v: pad(minutes), l: "Min" },
          { v: pad(seconds), l: "Sec" },
        ].map((c) => (
          <Card key={c.l}>
            <div className="flex flex-col items-center">
              <div className="text-28 font-bold tabular-nums">{c.v}</div>
              <div className="text-10 mt-1 tracking-wide text-white/70 uppercase">
                {c.l}
              </div>
            </div>
          </Card>
        ))}
      </div>
    </Dialog>
  );
};

export default ValueMomentUpsell;
